import { z as zod } from 'zod';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { useBoolean } from 'minimal-shared/hooks';
import { zodResolver } from '@hookform/resolvers/zod';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useRouter } from 'src/routes/hooks';
import { paths, newSearchParams } from 'src/routes/paths';

import { fCurrencySymbol } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';
import {
  _tags,
  PRODUCT_SIZE_OPTIONS,
  PRODUCT_GENDER_OPTIONS,
  PRODUCT_COLOR_NAME_OPTIONS,
  PRODUCT_CATEGORY_GROUP_OPTIONS,
} from 'src/_mock';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';
import { fileData } from 'src/components/file-thumbnail/utils';
import { Form, Field, schemaHelper } from 'src/components/hook-form';

import { ProductItem } from './product-item';

// ----------------------------------------------------------------------

export const NewProductSchema = (t, currencySymbol) => {
  const availableRequired = t('Quantity must not be less than or equal to 0');
  const priceIsRequired = `${t('Price must not be less than or equal to')} ${currencySymbol} 0`;

  const priceSchema = schemaHelper.nullableInput(
    zod.number({ coerce: true }).min(1, { message: priceIsRequired }),
    {
      // message for null value
      message: priceIsRequired,
    }
  );

  return zod.object({
    images: schemaHelper.files({ message: t('Images is required') }),
    name: zod.string().min(1, { message: t('Product name is required') }),
    description: schemaHelper
      .editor({ message: t('Description is required') })
      .min(100, { message: 'Description must be at least 100 characters' })
      .max(500, { message: 'Description must be less than 500 characters' }),
    code: zod.string().min(1, { message: t('Product code is required') }),
    sku: zod.string().min(1, { message: t('Product SKU is required') }),
    available: schemaHelper.nullableInput(
      zod.number({ coerce: true }).min(1, { message: availableRequired }),
      {
        // message for null value
        message: availableRequired,
      }
    ),
    colors: zod
      .string()
      .array()
      .min(1, { message: t('Choose at least one option') }),
    sizes: zod
      .string()
      .array()
      .min(1, { message: t('Choose at least one option') }),
    tags: zod
      .string()
      .array()
      .min(2, { message: t('Must have at least 2 tags') }),
    category: zod.string().min(1, { message: t('Category is required') }),
    gender: zod.array(zod.string()).min(1, { message: t('Choose at least one option') }),
    price: priceSchema,
    priceSale: priceSchema,
    // Not required
    shortDescription: zod.string(),
    taxes: zod.number({ coerce: true }).nullable(),
    shipping: zod.boolean(),
    newLabel: zod.object({ enabled: zod.boolean(), content: zod.string() }),
    saleLabel: zod.object({ enabled: zod.boolean(), content: zod.string() }),
    publish: zod.boolean(),
  });
};

const defaultValues = {
  name: '',
  description: '',
  shortDescription: '',
  images: [],
  //
  code: '',
  sku: '',
  price: 0,
  available: 0,
  priceSale: 0,
  tags: [],
  taxes: 0,
  gender: [],
  category: '',
  colors: [],
  sizes: [],
  shipping: false,
  newLabel: { enabled: false, content: '' },
  saleLabel: { enabled: false, content: 0 },
  publish: false,
};

export function ProductNewEditForm({ currentProduct }) {
  const router = useRouter();

  const previewProduct = useBoolean();

  const { t, currentLang } = useTranslate();

  const [taxes, setTaxes] = useState(0);
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [publish, setPublish] = useState(true);
  const [includeTaxes, setIncludeTaxes] = useState(false);
  const [includeNewLabel, setIncludeNewLabel] = useState(false);
  const [includeShipping, setIncludeShipping] = useState(false);

  const currencySymbol = fCurrencySymbol(currentLang.numberFormat);

  const methods = useForm({
    resolver: zodResolver(NewProductSchema(t, currencySymbol)),
    defaultValues,
    values: currentProduct,
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const handleChangePublish = (event) => setPublish(event.target.checked);
  const handleChangeNewLabel = (event) => setIncludeNewLabel(event.target.checked);
  const handleChangeIncludeTaxes = (event) => setIncludeTaxes(event.target.checked);
  const handleRemoveAllFiles = () => setValue('images', [], { shouldValidate: true });
  const handleChangeIncludeShipping = (event) => setIncludeShipping(event.target.checked);

  const handleChangeTaxes = (event) => {
    const { value } = event.target;
    setTaxes(Number(value > 0 && value < 100 ? value : 1));
  };

  const handleRemoveFile = (file) => {
    const images = values.images?.filter(({ path }) => path !== file.path) ?? [];
    setValue('images', images);
  };

  const handleDrop = (files) => {
    const images = values.images ?? [];
    const newImages = files.map((file) => ({ ...fileData(file), file }));
    setValue('images', [...images, ...newImages], { shouldValidate: true });
  };

  const handleChangePriceSale = (event) => {
    const { value } = event.target;
    setValue('priceSale', value);
    const originalPrice = Number(taxes ? (value * 1) / (1 - taxes / 100) : value);
    setPrice(Number(discount ? originalPrice / (1 - discount / 100) : originalPrice));
  };

  const handleChangeDiscount = (event) => {
    const { priceSale } = values;
    const { value } = event.target;
    const _discount = Number(value > 0 && value < 100 ? value : 0);
    setDiscount(_discount);
    const _price = _discount ? (priceSale * 1) / (1 - _discount / 100) : priceSale;
    setPrice(Number(_price));
  };

  useEffect(() => {
    if (includeTaxes) {
      setValue('taxes', 0);
    } else {
      setValue('taxes', currentProduct?.taxes || taxes);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct?.taxes, taxes, includeTaxes]);

  useEffect(() => {
    setValue('price', price);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price]);

  useEffect(() => {
    setValue('shipping', includeShipping);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeShipping]);

  useEffect(() => {
    setValue('newLabel.enabled', includeNewLabel);
    setValue('newLabel.content', includeNewLabel ? 'NEW' : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeNewLabel]);

  useEffect(() => {
    setValue('saleLabel.enabled', !!discount);
    setValue('saleLabel.content', discount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discount]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 700));

      console.info('DATA', {
        ...data,
        numberFormat: currentProduct?.numberFormat || currentLang.numberFormat,
      });

      reset();
      toast.success(t(currentProduct ? 'Successful update' : 'Successful creation'));
      router.push(`${paths.promotions}${newSearchParams({ country: currentLang.countryName })}`);
    } catch (error) {
      console.error(error);
    }
  });

  const renderPreview = () => (
    <Stack sx={{ top: 80, zIndex: 999, position: 'sticky' }}>
      <ProductItem
        preview
        product={{
          ...values,
          numberFormat: currentProduct?.numberFormat ?? currentLang.numberFormat,
        }}
      />
    </Stack>
  );

  const renderPreviewButton = () => (
    <Stack
      alignItems="center"
      sx={{ mb: 3, top: 8, zIndex: 999, position: 'sticky', display: { md: 'none' } }}
    >
      <Button variant="contained" onClick={previewProduct.onTrue} sx={{ width: 1 }}>
        {t('Preview')}
      </Button>
    </Stack>
  );

  const renderPreviewDrawer = () => (
    <SwipeableDrawer
      anchor="top"
      open={previewProduct.value}
      onOpen={previewProduct.onTrue}
      onClose={previewProduct.onFalse}
      sx={{ display: { md: 'none' } }}
    >
      {renderPreview()}

      <Box
        sx={{
          gap: 1,
          top: 100,
          width: 1,
          zIndex: 999,
          display: 'flex',
          alignItems: 'center',
          position: 'absolute',
          color: 'common.white',
          typography: 'caption',
          justifyContent: 'center',
        }}
      >
        <Iconify width={18} icon="solar:double-alt-arrow-up-bold-duotone" />
        {t('Swipe up to close')}
        <Iconify width={18} icon="solar:double-alt-arrow-up-bold-duotone" />
      </Box>
    </SwipeableDrawer>
  );

  const renderDetails = () => (
    <Card>
      <Stack spacing={3} sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Typography
            variant="subtitle2"
            sx={[(theme) => ({ [theme.breakpoints.down('md')]: { display: 'none' } })]}
          >
            {t('Photos')}
          </Typography>

          <Field.Upload
            multiple
            thumbnail
            name="images"
            maxSize={73145728}
            onDrop={handleDrop}
            onRemove={handleRemoveFile}
            onRemoveAll={handleRemoveAllFiles}
            accept={{ 'image/*': [], 'video/*': [] }}
          />
        </Stack>

        <Field.Text name="name" label={t('Product name')} />

        <Field.Text
          rows={4}
          multiline
          name="shortDescription"
          label={t('Short description')}
          placeholder={`${t("Write something that catches the public's attention")}...`}
        />

        <Stack spacing={1.5}>
          <Typography variant="subtitle2">{t('Description')}</Typography>
          <Field.Editor
            name="description"
            placeholder={`${t(
              'Includes data such as: benefits, instructions for use, dimensions, manufacturing data, etc'
            )}.`}
            sx={{ maxHeight: 480 }}
          />
        </Stack>
      </Stack>
    </Card>
  );

  const renderProperties = () => (
    <Card>
      <Stack spacing={3} sx={{ p: 3 }}>
        <Box
          columnGap={2}
          rowGap={3}
          display="grid"
          gridTemplateColumns={{ xs: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
        >
          <Field.Text name="code" label={t('Product code')} />

          <Field.Text name="sku" label={t('Product SKU')} />

          <Field.Text
            name="available"
            label={t('Available')}
            placeholder="0"
            type="number"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          <Field.Select
            name="category"
            label={t('Category')}
            slotProps={{
              select: { native: true },
              inputLabel: { shrink: true },
            }}
          >
            {PRODUCT_CATEGORY_GROUP_OPTIONS.map((category) => (
              <optgroup key={category.group} label={category.group}>
                {category.classify.map((classify) => (
                  <option key={classify} value={classify}>
                    {t(classify)}
                  </option>
                ))}
              </optgroup>
            ))}
          </Field.Select>

          <Field.MultiSelect
            checkbox
            name="colors"
            label={t('Colors')}
            options={PRODUCT_COLOR_NAME_OPTIONS}
          />

          <Field.MultiSelect
            checkbox
            name="sizes"
            label={t('Sizes')}
            options={PRODUCT_SIZE_OPTIONS}
          />
        </Box>

        <Field.Autocomplete
          name="tags"
          label={t('Tags')}
          placeholder={`+ ${t('Tags')}`}
          multiple
          freeSolo
          disableCloseOnSelect
          options={_tags.map((option) => option)}
          getOptionLabel={(option) => option}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              {t(option)}
            </li>
          )}
          renderValue={(selected, getTagProps) =>
            selected.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option}
                label={t(option)}
                size="small"
                color="info"
                variant="soft"
              />
            ))
          }
        />

        <Stack spacing={1}>
          <Typography variant="subtitle2">{t('Gender')}</Typography>
          <Field.MultiCheckbox row name="gender" options={PRODUCT_GENDER_OPTIONS} sx={{ gap: 2 }} />
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <FormControlLabel
          control={
            <Switch id="toggle-label" checked={includeNewLabel} onChange={handleChangeNewLabel} />
          }
          label={t('New label')}
        />
      </Stack>
    </Card>
  );

  const renderPricing = () => (
    <Card>
      <Stack spacing={3} sx={{ p: 3 }}>
        <Field.Text
          type="number"
          name="priceSale"
          placeholder="0.00"
          label={t('Sale price')}
          value={values.priceSale}
          onChange={handleChangePriceSale}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    {currencySymbol}
                  </Box>
                </InputAdornment>
              ),
            },
          }}
        />

        <FormControlLabel
          control={
            <Switch id="toggle-taxes" checked={includeTaxes} onChange={handleChangeIncludeTaxes} />
          }
          label={t('Price includes taxes')}
        />

        {!includeTaxes && (
          <Field.Text
            name="taxes"
            type="number"
            placeholder="0.00"
            label={t('Tax (%)')}
            value={taxes.toString()}
            onChange={handleChangeTaxes}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      %
                    </Box>
                  </InputAdornment>
                ),
              },
            }}
          />
        )}

        <Field.Text
          type="number"
          disabled={!price}
          placeholder="0.00"
          name="saleLabel.content"
          label={t('Discount (%)')}
          value={discount.toString()}
          onChange={handleChangeDiscount}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    %
                  </Box>
                </InputAdornment>
              ),
            },
          }}
        />

        <Field.Text
          disabled
          name="price"
          type="number"
          value={price}
          placeholder="0.00"
          label={t('Original price')}
          slotProps={{
            inputLabel: { shrink: true },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ color: 'text.disabled' }}>
                    {currencySymbol}
                  </Box>
                </InputAdornment>
              ),
            },
          }}
        />

        <FormControlLabel
          control={
            <Switch
              id="toggle-shipping"
              checked={includeShipping}
              onChange={handleChangeIncludeShipping}
            />
          }
          label={t('Price includes shipping')}
        />
      </Stack>
    </Card>
  );

  const renderActions = () => (
    <Stack spacing={3} direction="row" alignItems="center" flexWrap="wrap">
      <FormControlLabel
        control={
          <Switch
            checked={publish}
            onChange={handleChangePublish}
            slotProps={{ input: { id: 'publish-switch' } }}
          />
        }
        label={t('Publish')}
        sx={{ pl: 3, flexGrow: 1 }}
      />

      <Button type="submit" variant="contained" size="large" loading={isSubmitting}>
        {t(currentProduct ? 'Save changes' : 'Create promotion')}
      </Button>
    </Stack>
  );

  return (
    <Form methods={methods} onSubmit={onSubmit}>
      {renderPreviewButton()}
      {renderPreviewDrawer()}

      <Box gap={3} display="grid" gridTemplateColumns={{ xs: 'repeat(1, 1fr)', md: '30% 70%' }}>
        <Stack sx={[(theme) => ({ [theme.breakpoints.down('md')]: { display: 'none' } })]}>
          {renderPreview()}
        </Stack>

        <Stack spacing={3}>
          {renderDetails()}
          {renderProperties()}
          {renderPricing()}
          {renderActions()}
        </Stack>
      </Box>
    </Form>
  );
}
